import { z } from "zod";

export const transactionSchema = z.object({
  task_name: z.string(),
  ai_teammates: z.array(z.string()),
  timestamp: z.string(),
  credits_consumed: z.number(),
  user_email: z.string(),
});

// New schema for aggregated task data
export const aggregatedTaskSchema = z.object({
  task_name: z.string(),
  total_credits_consumed: z.number(),
  users: z.record(z.string(), z.number()),
  last_used_on: z.string(),
});

export const myUsageTrendsResponseSchema = z.object({
  graph_data: z.array(
    z.object({
      date: z.string(),
      total_credits_consumed: z.number(),
      breakdown: z.record(z.string(), z.number()),
    })
  ),
  top_ai_teammates: z.object({
    teammates: z.array(
      z.object({
        teammate: z.string(),
        credits_consumed: z.number(),
      })
    ),
    total_credits_consumed: z.number(),
  }),
});

export const orgUsageTrendsResponseSchema = z.object({
  graph_data: z.array(
    z.object({
      date: z.string(),
      total_credits_consumed: z.number(),
      breakdown: z.record(z.string(), z.number()),
    })
  ),
  top_ai_teammates: z.object({
    teammates: z.array(
      z.object({
        teammate: z.string(),
        credits_consumed: z.number(),
      })
    ),
    total_credits_consumed: z.number(),
  }),
  top_users: z.object({
    total_credits_consumed: z.number(),
    users: z.array(
      z.object({
        user_email: z.string(),
        display_name: z.string().nullable(),
        credits_consumed: z.number(),
      })
    ),
  }),
});

export const transactionGroupedByUserSchema = z.object({
  user_email: z.string(),
  display_name: z.string().nullable(),
  credits_consumed: z.number(),
  ai_teammates: z.array(
    z.object({
      teammate: z.string(),
      credits_consumed: z.number(),
    })
  ),
  last_used_on: z.string(),
});

export type Transaction = z.infer<typeof transactionSchema>;
export type AggregatedTask = z.infer<typeof aggregatedTaskSchema>;
export type TransactionGroupedByUser = z.infer<typeof transactionGroupedByUserSchema>;
export type MyUsageTrendsResponse = z.infer<typeof myUsageTrendsResponseSchema>;
export type OrgUsageTrendsResponse = z.infer<typeof orgUsageTrendsResponseSchema>;

// Plans Response Schema
export const PlansResponseSchema = z.object({
  used_credits: z.number(),
  remaining_credits: z.number(),
  extra_credits: z.number(),
  total_credits: z.number(),
  grant_remaining: z.number(),
  subscription_details: z.record(z.any()),
  expiry_projection: z.string().nullable(),
});

export type PlansResponse = z.infer<typeof PlansResponseSchema>;

// Checkout Session Request Schema
export const CheckoutSessionRequestSchema = z.object({
  plan_type: z.string(),
  tenure: z.string(),
});

export type CheckoutSessionRequest = z.infer<typeof CheckoutSessionRequestSchema>;

// Create Checkout Session Response Schema
export const CreateCheckoutSessionResponseSchema = z.object({
  checkout_url: z.string(),
});

export type CreateCheckoutSessionResponse = z.infer<typeof CreateCheckoutSessionResponseSchema>;

// Post Checkout Session Request Schema
export const PostCheckoutSessionRequestSchema = z.object({
  checkout_session_id: z.string(),
  cancelled: z.boolean(),
});

export type PostCheckoutSessionRequest = z.infer<typeof PostCheckoutSessionRequestSchema>;

// Downgrade Plan Request Schema
export const DowngradePlanRequestSchema = z.object({
  plan_type: z.string(),
  tenure: z.string(),
});

export type DowngradePlanRequest = z.infer<typeof DowngradePlanRequestSchema>;

// Downgrade Plan Feedback Request Schema
export const DowngradePlanFeedbackRequestSchema = z.object({
  feedback: z.string(),
});

export type DowngradePlanFeedbackRequest = z.infer<typeof DowngradePlanFeedbackRequestSchema>;

// Downgrade Plan Feedback Response Schema
export const DowngradePlanFeedbackResponseSchema = z.object({
  ok: z.boolean(),
});

export type DowngradePlanFeedbackResponse = z.infer<typeof DowngradePlanFeedbackResponseSchema>;

// Credit Usage Response Schema
export const CreditUsageResponseSchema = z.object({
  ok: z.boolean(),
  expired: z.boolean(),
  plan_end_date: z.string().optional().nullable(),
  total_available_executions: z.number(),
  total_executions: z.number(),
  plan_total_executions: z.number(),
  plan_available_executions: z.number(),
  used_executions: z.number(),
  available_grant: z.number(),
  granted_executions: z.number(),
  extra_executions: z.number(),
  current_start_date: z.string(),
  next_refresh_date: z.string(),
  credit_type: z.string(),
  tenure: z.string(),
  cost_per_execution: z.number(),
  total_cost: z.number(),
  free_grant_remaining: z.boolean(),
  downgraded_info: z
    .object({
      plan_name: z.string(),
      plan_display_name: z.string(),
      current_subscription_end_date: z.string(),
      tenure: z.string(),
    })
    .optional()
    .nullable(),
  plan_to_edition_map: z.record(z.string()),
  current_edition: z.string().optional().nullable(),
  expiry_projection: z.string().optional().nullable(),
});

export type CreditUsageResponse = z.infer<typeof CreditUsageResponseSchema>;

// All Plans Response Schema
export const AllPlansResponseSchema = z.object({
  plans: z.array(
    z.object({
      plan_name: z.string(),
      num_executions: z.union([z.number(), z.string()]),
      price: z.union([z.number(), z.string()]),
      token_allowance: z.union([z.number(), z.string()]).default(0),
    })
  ),
  current_tenure: z.string(),
  current_execuiton_count: z.number(),
  current_price: z.number(),
  subscription_management_allowed: z.boolean(),
});

export type AllPlansResponse = z.infer<typeof AllPlansResponseSchema>;

// ---------------------------------------------------------------------------
// Wallet schemas
// ---------------------------------------------------------------------------

export const WalletTransactionSchema = z.object({
  id: z.number(),
  transaction_type: z.string(),
  amount_cents: z.number(),
  balance_after_cents: z.number(),
  tokens: z.number().nullable(),
  description: z.string().nullable(),
  created_on: z.string(),
});

export type WalletTransaction = z.infer<typeof WalletTransactionSchema>;

export const WalletResponseSchema = z.object({
  balance_cents: z.number(),
  balance_tokens: z.number().optional().default(0),
  spend_cap_cents: z.number().nullable().optional(),
  spend_this_period_cents: z.number(),
  auto_reload_enabled: z.boolean().optional().default(false),
  auto_reload_amount_cents: z.number().optional().default(0),
  auto_reload_threshold_cents: z.number().optional().default(0),
  overage_rate_cents_per_million: z.number().optional().default(0),
  transactions: z.array(WalletTransactionSchema),
});

export type WalletResponse = z.infer<typeof WalletResponseSchema>;

// Token Usage Response Schema
export const TokenUsageResponseSchema = z.object({
  credit_type: z.string(),
  unlimited: z.boolean().optional(),
  // Aggregate totals (grant + monthly allowance)
  total_tokens: z.number().optional().default(0),
  total_consumed: z.number().optional().default(0),
  total_remaining: z.number().optional().default(0),
  // Monthly allowance breakdown
  tokens_consumed: z.number(),
  tokens_included: z.number(),
  tokens_remaining: z.number(),
  overage_tokens: z.number(),
  overage_rate_cents_per_million: z.number(),
  // Grant breakdown
  grant_tokens_total: z.number().optional().default(0),
  grant_tokens_consumed: z.number().optional().default(0),
  grant_tokens_remaining: z.number(),
  period_start: z.string().nullable(),
  period_end: z.string().nullable(),
  wallet_balance_cents: z.number(),
  wallet_spend_cap_cents: z.number().nullable().optional(),
  wallet_spend_this_period_cents: z.number(),
});

export type TokenUsageResponse = z.infer<typeof TokenUsageResponseSchema>;

export const WalletDepositResponseSchema = z.object({
  checkout_url: z.string(),
});

export type WalletDepositResponse = z.infer<typeof WalletDepositResponseSchema>;
